import { Component } from '@theme/component';

const normalise = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/organi[sz]/g, 'organis').replace(/[^a-z0-9£.\s-]/g, ' ').replace(/\s+/g, ' ').trim();
const stopWords = new Set('a an the i am me my we our you your find looking look for want would like need please show some something any can could have do does is are it items item products product with and or of in on to that this essentials ideas under below up less than gbp pounds cheaper cheapest budget only more'.split(' '));
const groups = {
  pet: ['pet', 'dog', 'cat', 'bird'],
  winter: ['winter', 'frost', 'snow', 'thermal', 'beanie', 'glove'],
  phone: ['phone', 'mobile'],
  storage: ['storage', 'organis', 'wardrobe', 'shelf', 'rack'],
  gift: ['gift', 'christmas', 'greeting'],
  travel: ['travel', 'picnic', 'luggage', 'car'],
};

export function findPreviewMatches(items, request, previous = null) {
  const text = normalise(request);
  const budgetMatch = text.match(/(?:under|below|up to|less than|budget|maximum|max)\s*£?\s*(\d+(?:\.\d{1,2})?)/);
  const terms = text.split(' ').filter(term => term.length > 1 && !stopWords.has(term) && !/^£?\d+(?:\.\d+)?$/.test(term));
  const queryTerms = terms.length ? terms : previous?.terms || [];
  const budget = budgetMatch ? Math.round(Number(budgetMatch[1]) * 100) : previous?.budget ?? null;
  const stem = word => word.length > 3 ? word.replace(/ies$/, 'y').replace(/s$/, '') : word;
  const scored = items.map(item => {
    const words = normalise([item.title, item.sku, item.department, item.subcategory].join(' ')).split(/[\s-]+/).map(stem);
    let score = 0;
    for (const term of queryTerms) {
      const key = stem(term);
      const candidates = groups[key] || [key];
      if (!candidates.some(candidate => words.some(word => word.includes(candidate)))) return null;
      score += words.some(word => word === key) ? 3 : 1;
    }
    if (!queryTerms.length || !Number.isFinite(item.amount) || (budget !== null && item.amount > budget)) return null;
    return { item, score };
  }).filter(Boolean).sort((a, b) => /cheap|lowest|low price/.test(text) ? a.item.amount - b.item.amount : b.score - a.score);
  return { items: scored.map(result => result.item), terms: queryTerms, budget };
}

class MEGAssistant extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.config = JSON.parse(this.querySelector('[data-assistant-config]').textContent);
    this.labels = this.config.labels;
    this.disclosure = this.querySelector('#meg-help');
    this.trigger = this.disclosure.querySelector(':scope > summary');
    this.log = this.querySelector('[data-assistant-log]');
    this.form = this.querySelector('[data-assistant-form]');
    this.input = this.form.querySelector('input');
    this.addEventListener('click', this.handleClick, { signal });
    this.form.addEventListener('submit', event => {
      event.preventDefault();
      const request = this.input.value.trim();
      if (!request) return;
      this.input.value = '';
      this.respond(request);
    }, { signal });
    this.querySelector('[data-assistant-help-search]').addEventListener('input', event => {
      const term = normalise(event.target.value);
      let found = false;
      for (const faq of this.querySelectorAll('[data-assistant-faq]')) {
        faq.hidden = !normalise(faq.textContent).includes(term);
        if (faq.hidden) faq.open = false;
        else found = true;
      }
      this.querySelector('[data-assistant-help-empty]').hidden = found;
    }, { signal });
    document.addEventListener('pointerdown', event => {
      if (this.disclosure.open && event.target instanceof Node && !this.contains(event.target)) this.close(false);
    }, { capture: true, signal });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.disclosure.open) {
        event.preventDefault();
        this.close(true);
      }
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.requestId = (this.requestId || 0) + 1;
  }

  handleClick = async event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const close = target.closest('[data-assistant-close]');
    if (close) return this.close(true);
    const switcher = target.closest('[data-assistant-switch]');
    if (switcher) return this.switchView(switcher.dataset.assistantSwitch, !switcher.closest('nav'));
    const query = target.closest('[data-assistant-query]');
    if (query) return this.respond(query.dataset.assistantQuery);
    if (target.closest('[data-assistant-reset]')) {
      this.requestId = (this.requestId || 0) + 1;
      this.context = null;
      this.log.replaceChildren();
      this.message(this.labels.greeting);
      this.input.focus({ preventScroll: true });
      return;
    }
    if (target.closest('[data-assistant-more]')) {
      this.showResults(this.labels.more_reply, true);
      return;
    }
    const link = target.closest('a');
    if (!link || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const destination = new URL(link.href, location.href);
    if (destination.origin !== location.origin) return;
    this.close(false);
    if (destination.pathname !== location.pathname || !destination.hash) return;
    const anchor = destination.hash.slice(1);
    const range = document.querySelector('#meg-new-range meg-catalogue-browser');
    if (anchor.startsWith('meg-preview-') && range) {
      event.preventDefault();
      history.pushState({}, '', destination.href);
      await range.revealItem(anchor);
    }
  };

  close(restoreFocus) {
    this.disclosure.open = false;
    if (restoreFocus) this.trigger.focus({ preventScroll: true });
  }

  switchView(view, focus = false) {
    for (const element of this.querySelectorAll('[data-assistant-view]')) element.hidden = element.dataset.assistantView !== view;
    for (const button of this.querySelectorAll('nav [data-assistant-switch]')) button.setAttribute('aria-pressed', String(button.dataset.assistantSwitch === view));
    if (focus && view === 'discover') this.input.focus({ preventScroll: true });
  }

  message(text, user = false) {
    const message = document.createElement('div');
    message.className = 'meg-assistant__message' + (user ? ' meg-assistant__message--user' : '');
    const speaker = document.createElement('span');
    speaker.className = 'meg-assistant__speaker';
    speaker.textContent = user ? this.labels.you : this.labels.assistant;
    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    message.append(speaker, paragraph);
    this.log.append(message);
    while (this.log.children.length > 24) this.log.firstElementChild.remove();
    this.log.scrollTop = this.log.scrollHeight;
    return message;
  }

  link(message, label, path) {
    const link = document.createElement('a');
    link.href = path;
    link.textContent = label;
    const paragraph = document.createElement('p');
    paragraph.append(link);
    message.append(paragraph);
  }

  async catalogue() {
    if (this.items) return this.items;
    const { loadPreviewCatalogue } = await import(this.config.feedLoader);
    const data = await loadPreviewCatalogue([this.config.catalogueUrl, this.config.extraCatalogueUrl]);
    this.items = data.items;
    return this.items;
  }

  async respond(request) {
    this.switchView('discover');
    this.message(request, true);
    const token = this.requestId = (this.requestId || 0) + 1;
    const text = normalise(request);
    const answers = [
      [/\b(track|tracking|courier|royal mail|dpd|evri|dhl|ups)\b/, 'answer_tracking', 'tracking', this.config.helpCentreUrl + '#help-tracking'],
      [/\b(delivery|shipping|dispatch|postcode|arrive)\b/, 'answer_delivery', 'help_centre', this.config.helpCentreUrl + '#help-delivery'],
      [/\b(return|returns|refund|faulty|damaged|cancel|cancellation)\b/, 'answer_person', 'help_centre', this.config.helpCentreUrl + '#help-returns'],
      [/\b(order|orders|account)\b/, 'answer_order', 'account', this.config.accountUrl],
      [/\b(person|human|team|email|staff|contact)\b/, 'answer_person', 'contact', this.config.rootUrl + 'pages/contact'],
      [/\b(mix|guided|shortlist)\b/, 'answer_guided', 'mix', this.config.rootUrl + '#meg-mix-match'],
      [/\b(can i buy|ordering open|buy now|are you open|in stock|available to buy)\b/, 'answer_buy', 'browse_all', this.config.rootUrl + '#meg-new-range'],
    ];
    for (const [pattern, answer, label, url] of answers) {
      if (!pattern.test(text)) continue;
      const message = this.message(this.labels[answer]);
      this.link(message, this.labels[label], url);
      return;
    }
    if (/^(hi|hello|hey|thanks|thank you)$/.test(text)) return this.message(this.labels.greeting);
    if (/^(more|show more|more ideas)$/.test(text) && this.context) return this.showResults(this.labels.more_reply, true);
    const loading = this.message(this.labels.loading);
    try {
      const items = await this.catalogue();
      if (token !== this.requestId || !this.isConnected) { loading.remove(); return; }
      const result = findPreviewMatches(items, request, this.context);
      this.context = { ...result, offset: 0 };
      loading.remove();
      if (!result.terms.length) return this.message(this.labels.empty_request);
      if (!result.items.length) return this.message(this.labels.no_results);
      this.showResults(this.labels.results.replace('__COUNT__', String(result.items.length)));
    } catch (error) {
      loading.remove();
      if (error.name !== 'AbortError' && token === this.requestId && this.isConnected) this.message(this.labels.load_error);
    }
  }

  showResults(text, more = false) {
    const context = this.context;
    if (!context || context.offset >= context.items.length) return this.message(this.labels.no_more);
    this.log.querySelectorAll('[data-assistant-more]').forEach(button => button.remove());
    const message = this.message(text);
    if (context.budget !== null && !more) {
      const budget = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(context.budget / 100);
      const note = document.createElement('p');
      note.textContent = this.labels.budget_note.replace('__BUDGET__', budget);
      message.append(note);
    }
    for (const item of context.items.slice(context.offset, context.offset + 3)) {
      const link = document.createElement('a');
      link.className = 'meg-assistant__product';
      link.href = this.config.rootUrl + '#' + encodeURIComponent(item.anchor);
      const picture = document.createElement('img');
      const source = new URL(item.image, location.href);
      if (source.protocol === 'https:' && source.hostname === 'cdn.shopify.com') picture.src = source.href;
      picture.alt = '';
      picture.loading = 'lazy';
      picture.width = 48;
      picture.height = 48;
      const body = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = item.title;
      const price = document.createElement('small');
      price.textContent = this.labels.planned_price.replace('__PRICE__', new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.amount / 100));
      const badge = document.createElement('small');
      badge.textContent = this.labels.product_badge + ' · ' + item.sku;
      body.append(title, price, badge);
      link.append(picture, body);
      message.append(link);
    }
    context.offset += 3;
    if (context.offset < context.items.length) {
      const moreButton = document.createElement('button');
      moreButton.type = 'button';
      moreButton.className = 'meg-assistant__more';
      moreButton.dataset.assistantMore = '';
      moreButton.textContent = this.labels.more;
      message.append(moreButton);
    }
    this.log.scrollTop = this.log.scrollHeight;
  }
}

if (!customElements.get('meg-assistant')) customElements.define('meg-assistant', MEGAssistant);

